// attempt-finished-journal-reader.ts
//
// Orchestrator recovery (CLI 3.40.0): the attempts that STOPPED a ticket, read
// from `journal.jsonl` WITHOUT the writer lease — `status --json` runs while
// `run --detach` holds it, and opening the writer path there is Bug H
// (JournalLeaseError; see mission-store-read-only.ts). Same lock-free
// `scanJournal` contract as mission-cost-journal-reader.ts. Shared by
// `status --json`, `run --json` and the recovery scan.
import { join } from "node:path";
import { type BlockedBy, parseBlockedBy } from "./escalation-blocked-by.js";
import { type JournalRecord, scanJournal } from "./journal-scan-report.js";
import { MISSION_JOURNAL_FILE_NAME } from "./mission-store.js";

export interface StopAttemptRecord {
  /** Journal seq of the `attempt_finished` that carried `blocked_by`. */
  seq: number;
  ticket_id: string;
  blocked_by: BlockedBy;
  category: string;
  /** `reasons[0]` of that attempt (already redacted by retry-policy) — the recovery brief's last reason. */
  last_reason?: string;
}

export interface StopAttemptScan {
  /** Every stop, in seq order. */
  stops: StopAttemptRecord[];
  /** Stops whose ticket is STILL blocked by that stop (no later state change / attempt). */
  standing: StopAttemptRecord[];
}

const str = (value: unknown): string | undefined => (typeof value === "string" ? value : undefined);

function lastReasonOf(reasons: unknown): { last_reason?: string } {
  const first = Array.isArray(reasons) ? str(reasons[0]) : undefined;
  return first === undefined ? {} : { last_reason: first };
}

/** Best-effort: an unreadable journal reads as no records (status never throws for this). */
export async function readJournalRecordsLockFree(controlRoot: string): Promise<JournalRecord[]> {
  const scan = await scanJournal(join(controlRoot, MISSION_JOURNAL_FILE_NAME)).catch(
    () => undefined,
  );
  return scan?.records ?? [];
}

export async function readStopAttempts(controlRoot: string): Promise<StopAttemptScan> {
  return stopAttemptsFromRecords(await readJournalRecordsLockFree(controlRoot));
}

export function stopAttemptsFromRecords(records: readonly JournalRecord[]): StopAttemptScan {
  const stops: StopAttemptRecord[] = [];
  const status = new Map<string, string>();
  const standing = new Map<string, StopAttemptRecord>();
  for (const { seq, delta } of records) {
    const ticket = str(delta.ticket);
    if (ticket === undefined) continue;
    if (delta.kind === "ticket_state_changed") {
      // journalAttemptOutcome commits the state change BEFORE attempt_finished,
      // so a stop re-arms right after; any other change clears it.
      status.set(ticket, str(delta.status) ?? "unknown");
      standing.delete(ticket);
      continue;
    }
    if (delta.kind !== "attempt_finished") continue;
    if (delta.blocked_by === undefined) {
      standing.delete(ticket);
      continue;
    }
    const record: StopAttemptRecord = {
      seq,
      ticket_id: ticket,
      blocked_by: parseBlockedBy(delta.blocked_by),
      category: str(delta.category) ?? "unknown",
      ...lastReasonOf(delta.reasons),
    };
    stops.push(record);
    if (status.get(ticket) === "blocked") standing.set(ticket, record);
  }
  return { stops, standing: [...standing.values()].sort((a, b) => a.seq - b.seq) };
}
